/**
 * How a plan row's quantity relates to its door count.
 *
 * Two shapes share this grid, and nothing on a row says which one it is:
 *
 * - **per_store** — the "Add sales source" dialog's long-standing shape: `cases_sold` is the rate
 *   at one store, and dollars are `stores × cases × cost`.
 * - **total** — what the importers write (Sanzo's weekly Confido plan): `cases_sold` /
 *   `units_sold` are the whole account's volume, dollars are `cases × cost`, and the per-store
 *   rate is derived (`quantity ÷ stores`, the grid's "Per store" line).
 *
 * Anything that writes or reprices a row has to know which it is looking at. Treating a total
 * row as per-store is how a store-count cut moved Walmart's dollars and left its units where
 * they were, and how a new QFC row landed in Sanzo's plan showing 3 cases against $692.
 */
export type QuantityBasis = "per_store" | "total"

interface BasisMetrics {
  store_count?: number | null
  cases_sold?: number | null
  units_sold?: number | null
  volume_dollars?: number | null
  case_cost?: number | null
}

interface BasisPoint {
  metrics?: BasisMetrics | null
  source_kind?: string
  is_actual?: boolean
}

interface BasisNode {
  stable_key?: string
  metadata?: { opportunity?: unknown; [key: string]: unknown } | null
  children?: BasisNode[]
  sales_data?: BasisPoint[]
}

/** Enough decisive rows to call a plan's basis; a plan is one shape or the other, not a blend. */
const BASIS_VOTE_LIMIT = 400

/**
 * Which shape one data point is, read from its own arithmetic: whichever of `cases × cost` and
 * `stores × cases × cost` its dollars sit closer to. `null` when the row cannot say — one door
 * or none (both readings agree), no cost, or no volume.
 */
export function point_quantity_basis(metrics: BasisMetrics | null | undefined): QuantityBasis | null {
  const stores = Number(metrics?.store_count ?? 0)
  const cases = Number(metrics?.cases_sold ?? 0)
  const cost = Number(metrics?.case_cost ?? 0)
  const dollars = Number(metrics?.volume_dollars ?? 0)
  if (!(stores > 1 && cases > 0 && cost > 0 && dollars > 0)) return null
  const as_total = cases * cost
  const as_per_store = stores * as_total
  return Math.abs(dollars - as_total) <= Math.abs(dollars - as_per_store) ? "total" : "per_store"
}

function is_plan_point(point: BasisPoint): boolean {
  return point.source_kind !== "actual" && point.is_actual !== true
}

/**
 * The shape a plan is in, by majority of its leaf rows that can say. A plan with nothing
 * decisive — empty, or dollars only — reads as `total`: 100 doors at 1 case a month is 100 cases
 * on the grid, which is what a planner starting a blank year expects to see (the per-store rate
 * still shows on the grid's "Per store" line). It used to read as `per_store`, and a new plan's
 * cases came out 100× under its revenue.
 */
export function plan_quantity_basis(roots: BasisNode[] | undefined): QuantityBasis {
  let total = 0
  let per_store = 0

  function walk(node: BasisNode): void {
    if (total + per_store >= BASIS_VOTE_LIMIT) return
    const children = node.children ?? []
    if (children.length > 0) {
      children.forEach(walk)
      return
    }
    for (const point of node.sales_data ?? []) {
      if (!is_plan_point(point)) continue
      const basis = point_quantity_basis(point.metrics)
      if (basis === "total") total += 1
      if (basis === "per_store") per_store += 1
      if (total + per_store >= BASIS_VOTE_LIMIT) return
    }
  }

  ;(roots ?? []).forEach(walk)
  return per_store > total ? "per_store" : "total"
}

/** How the plan already carries one SKU, so a new row for it can be written the same way. */
export interface SkuPlanReference {
  /** Price of one case on the plan's own rows for this SKU. */
  case_cost: number
  /**
   * The plan's units in one case — dollars per case ÷ dollars per unit — or `null` when the plan
   * carries no unit count for this SKU. This is the plan's own unit, which need not be the
   * catalog's: Sanzo counts a 6-pack as one unit *and* one case, while the catalog's
   * `units_per_case` for it is 6 cans.
   */
  units_per_case: number | null
}

/** A ratio this close to a whole number is that whole number plus rounding in the stored dollars. */
const WHOLE_UNITS_TOLERANCE = 0.02

/**
 * Read a SKU's price and unit pack off the rows the plan already has for it (same `stable_key`,
 * any customer). `null` when the SKU is not in the plan yet or none of its rows carry a cost.
 */
export function sku_plan_reference(
  roots: BasisNode[] | undefined,
  stable_key: string | undefined,
): SkuPlanReference | null {
  if (!stable_key) return null
  const cost_counts = new Map<number, number>()
  const by_cost = new Map<number, { dollars_cents: number; units: number }>()

  function walk(node: BasisNode): void {
    if (node.stable_key === stable_key && !node.metadata?.opportunity) {
      for (const point of node.sales_data ?? []) {
        if (!is_plan_point(point)) continue
        const cost = Number(point.metrics?.case_cost ?? 0)
        if (!(cost > 0)) continue
        cost_counts.set(cost, (cost_counts.get(cost) ?? 0) + 1)
        const units = Number(point.metrics?.units_sold ?? 0)
        const dollars = Number(point.metrics?.volume_dollars ?? 0)
        if (units > 0 && dollars > 0) {
          const sums = by_cost.get(cost) ?? { dollars_cents: 0, units: 0 }
          sums.dollars_cents += Math.round(dollars * 100)
          sums.units += units
          by_cost.set(cost, sums)
        }
      }
    }
    ;(node.children ?? []).forEach(walk)
  }
  ;(roots ?? []).forEach(walk)

  let case_cost = 0
  let best = 0
  for (const [cost, count] of cost_counts) {
    if (count > best) {
      best = count
      case_cost = cost
    }
  }
  if (!(case_cost > 0)) return null

  const sums = by_cost.get(case_cost)
  if (!sums || sums.units <= 0) return { case_cost, units_per_case: null }
  const dollars_per_unit = sums.dollars_cents / 100 / sums.units
  const ratio = case_cost / dollars_per_unit
  const whole = Math.round(ratio)
  const units_per_case =
    whole >= 1 && Math.abs(ratio - whole) / whole <= WHOLE_UNITS_TOLERANCE ? whole : Math.round(ratio * 100) / 100
  return { case_cost, units_per_case }
}

/**
 * The part of a case a zero-case row really holds. Cases are stored whole, so a row with less
 * than half a case of volume holds `0` beside real dollars; its dollars and case price say how
 * much of a case that is. Zero for any row that has whole cases, no dollars or no price — and for
 * a row whose dollars would make a whole case or more, which is not cases × price at all.
 */
export function implied_part_case(cases_sold: number | null | undefined, volume_dollars?: number | null, case_cost?: number | null): number {
  if (Number(cases_sold ?? 0) !== 0) return 0
  const dollars_cents = Math.round(Number(volume_dollars ?? 0) * 100)
  const cost_cents = Math.round(Number(case_cost ?? 0) * 100)
  if (!(dollars_cents > 0 && cost_cents > 0)) return 0
  const implied = dollars_cents / cost_cents
  return implied < 1 ? implied : 0
}

interface PartCaseMetrics {
  cases_sold?: number | null
  volume_dollars?: number | null
  case_cost?: number | null
  part_cases?: number | null
}

/**
 * Part-cases a row stands for: an aggregate carries the sum its roll-up collected
 * (`part_cases`), a leaf its own implied part-case.
 */
export function metrics_part_cases(metrics: PartCaseMetrics | null | undefined): number {
  if (!metrics) return 0
  if (metrics.part_cases != null) return Number(metrics.part_cases) || 0
  return implied_part_case(metrics.cases_sold, metrics.volume_dollars, metrics.case_cost)
}

/**
 * A case count for display: the whole number when there is one, otherwise the part-cases behind a
 * zero to two decimals (`0.25`), so volume under half a case does not read as "no volume" — on a
 * leaf cell, a parent row or a total alike.
 */
export function cases_display(cases_sold: number | null | undefined, part_cases?: number | null): string {
  const cases = Number(cases_sold ?? 0)
  if (cases !== 0) return format_cases(cases)
  const part = Number(part_cases ?? 0)
  return part > 0 ? part.toFixed(2) : "0"
}

/**
 * Cases to the hundredth: a per-store rate of 1.2 across 7 doors is 8.4 cases, and the grid, the
 * roll-ups and the save all carry that fraction rather than a whole-case rounding of it.
 */
export function round_cases(cases: number): number {
  const n = Number(cases)
  if (!Number.isFinite(n)) return 0
  return Math.round(n * 100) / 100
}

/**
 * The pack size a node carries (`metadata.units_per_case`, written by the SKU picker and the
 * catalog add), or `null` when it carries none or a nonsense value.
 */
export function node_units_per_case(node: { metadata?: { units_per_case?: unknown } | null } | null | undefined): number | null {
  const raw = node?.metadata?.units_per_case
  const n = Number(raw)
  return raw !== undefined && raw !== null && Number.isFinite(n) && n > 0 ? n : null
}

/** Units a case count represents, whole units. `null` when the pack size is unknown. */
export function units_from_cases(cases: number, units_per_case: number | null | undefined): number | null {
  if (!units_per_case || !(units_per_case > 0)) return null
  const n = Number(cases)
  if (!Number.isFinite(n)) return null
  return Math.round(n * units_per_case)
}

/**
 * Cases a unit count converts back to — fractional, to the hundredth, so 18 units of a 12-pack
 * are 1.5 cases on the row rather than 2 (or 1). `null` when the pack size is unknown.
 */
export function cases_from_units(units: number, units_per_case: number | null | undefined): number | null {
  if (!units_per_case || !(units_per_case > 0)) return null
  const n = Number(units)
  if (!Number.isFinite(n)) return null
  return round_cases(n / units_per_case)
}

/** A case count for display: whole numbers as they are, fractions to two decimals ("8.4", "0.25"). */
export function format_cases(cases: number): string {
  return round_cases(cases).toLocaleString("en-US", { maximumFractionDigits: 2 })
}

/** Trims float dust and trailing zeros off a quantity for display ("12", "1.5"). */
function plain_number(value: number): string {
  return String(Math.round(value * 100) / 100)
}

export interface CaseBasisHint {
  /** What a case of this SKU is, in the plan's own terms. */
  text: string
  /** Set when the price typed is not the plan's price for a case — a different pack is being priced. */
  warning?: string
}

/**
 * Says, next to the cases field, what "a case" of the picked SKU is — so a planner who thinks in
 * trays is not silently entering 6-packs. The plan's own rows decide when it has any (they are
 * what the new row will sit beside); otherwise the catalog's pack; `null` when neither knows.
 */
export function case_basis_hint(input: {
  reference: SkuPlanReference | null
  catalog_units_per_case?: number | null
  /** Cases per store per period, as typed. */
  cases_per_store?: number | null
  /** Price per case, as typed. */
  price_per_case?: number | null
  /** "week" or "month". */
  span: string
}): CaseBasisHint | null {
  const { reference, span } = input
  if (!reference) {
    const catalog = Number(input.catalog_units_per_case ?? 0)
    return catalog > 0 ? { text: `The catalog's case for this SKU holds ${plain_number(catalog)}. Cases and price are both per that case.` } : null
  }

  const cost = `$${reference.case_cost.toFixed(2)}`
  const units = reference.units_per_case
  const cases = Number(input.cases_per_store ?? 0)
  let text: string
  if (units == null) {
    text = `This plan prices a case of this SKU at ${cost}.`
  } else if (units === 1) {
    text = `In this plan a case of this SKU is counted as 1 unit (${cost}), so cases and units are the same number here.`
  } else {
    text = `In this plan 1 case of this SKU is ${plain_number(units)} units, at ${cost} a case.`
    if (cases > 0) {
      text += ` ${plain_number(cases)} ${cases === 1 ? "case" : "cases"} per store is ${plain_number(cases * units)} units per store per ${span}.`
    }
  }

  const price = Number(input.price_per_case ?? 0)
  const off_plan_price = price > 0 && Math.abs(price - reference.case_cost) / reference.case_cost > 0.01
  return off_plan_price
    ? { text, warning: `The rest of this plan prices this SKU at ${cost} a case. $${price.toFixed(2)} prices a different pack.` }
    : { text }
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  function point(metrics: BasisMetrics, extra: Partial<BasisPoint> = {}): BasisPoint {
    return { metrics, source_kind: "forecast", ...extra }
  }

  describe("point_quantity_basis", () => {
    it("reads an imported row as total: dollars are cases × cost", () => {
      // Sanzo, Walmart, Lychee 6-pack, 2026-W45.
      expect(point_quantity_basis({ store_count: 1313, cases_sold: 1116, volume_dollars: 5736.24, case_cost: 5.14 })).toBe("total")
    })

    it("reads a dialog row as per-store: dollars are stores × cases × cost", () => {
      expect(point_quantity_basis({ store_count: 100, cases_sold: 90, volume_dollars: 90000, case_cost: 10 })).toBe("per_store")
    })

    it("still reads total when whole-case rounding hides most of the volume", () => {
      // 11 single cans is 0.92 of a 12-pack case, stored as 1 case against $12.43.
      expect(point_quantity_basis({ store_count: 27, cases_sold: 1, units_sold: 11, volume_dollars: 12.43, case_cost: 13.56 })).toBe("total")
    })

    it("cannot say for one door, no cost, or no volume", () => {
      expect(point_quantity_basis({ store_count: 1, cases_sold: 5, volume_dollars: 50, case_cost: 10 })).toBeNull()
      expect(point_quantity_basis({ store_count: 40, cases_sold: 5, volume_dollars: 50, case_cost: 0 })).toBeNull()
      expect(point_quantity_basis({ store_count: 40, cases_sold: 0, volume_dollars: 0, case_cost: 10 })).toBeNull()
      expect(point_quantity_basis(undefined)).toBeNull()
    })
  })

  describe("plan_quantity_basis", () => {
    const total_leaf: BasisNode = {
      sales_data: [point({ store_count: 1313, cases_sold: 1116, volume_dollars: 5736.24, case_cost: 5.14 })],
    }
    const per_store_leaf: BasisNode = {
      sales_data: [point({ store_count: 100, cases_sold: 90, volume_dollars: 90000, case_cost: 10 })],
    }

    it("follows the majority of leaf rows", () => {
      expect(plan_quantity_basis([{ children: [total_leaf, total_leaf, per_store_leaf] }])).toBe("total")
      expect(plan_quantity_basis([{ children: [per_store_leaf] }])).toBe("per_store")
    })

    it("ignores parents, whose rolled-up stores and cases prove nothing", () => {
      const parent: BasisNode = {
        // A parent's own row would read as per-store; only its leaves count.
        sales_data: [point({ store_count: 100, cases_sold: 90, volume_dollars: 90000, case_cost: 10 })],
        children: [total_leaf],
      }
      expect(plan_quantity_basis([parent])).toBe("total")
    })

    it("ignores actuals and reads an undecided plan as totals", () => {
      const actual_only: BasisNode = {
        sales_data: [point({ store_count: 1313, cases_sold: 1116, volume_dollars: 5736.24, case_cost: 5.14 }, { source_kind: "actual" })],
      }
      expect(plan_quantity_basis([actual_only])).toBe("total")
      expect(plan_quantity_basis([])).toBe("total")
      expect(plan_quantity_basis(undefined)).toBe("total")
    })

    it("still follows a plan whose rows are per-store", () => {
      expect(plan_quantity_basis([{ children: [per_store_leaf, per_store_leaf, total_leaf] }])).toBe("per_store")
    })
  })

  describe("sku_plan_reference", () => {
    function sku(stable_key: string, points: BasisPoint[], extra: Partial<BasisNode> = {}): BasisNode {
      return { stable_key, sales_data: points, ...extra }
    }

    it("finds a 6-pack planned as one unit per case", () => {
      const roots = [
        {
          children: [
            sku("sku:840269200229", [point({ store_count: 1313, cases_sold: 1116, units_sold: 1116, volume_dollars: 5736.24, case_cost: 5.14 })]),
          ],
        },
      ]
      expect(sku_plan_reference(roots, "sku:840269200229")).toEqual({ case_cost: 5.14, units_per_case: 1 })
    })

    it("finds single cans planned twelve to a case, through whole-case rounding", () => {
      const roots = [
        sku("sku:840269200144", [
          point({ store_count: 27, cases_sold: 1, units_sold: 11, volume_dollars: 12.43, case_cost: 13.56 }),
          point({ store_count: 6, cases_sold: 3, units_sold: 32, volume_dollars: 36.16, case_cost: 13.56 }),
        ]),
      ]
      expect(sku_plan_reference(roots, "sku:840269200144")).toEqual({ case_cost: 13.56, units_per_case: 12 })
    })

    it("reports no unit pack when the plan carries no units for the SKU", () => {
      const roots = [sku("sku:1", [point({ store_count: 10, cases_sold: 5, volume_dollars: 50, case_cost: 10 })])]
      expect(sku_plan_reference(roots, "sku:1")).toEqual({ case_cost: 10, units_per_case: null })
    })

    it("is null for a SKU the plan does not have, and never reads an opportunity's weighted rows", () => {
      const roots = [
        sku("sku:1", [point({ store_count: 10, cases_sold: 5, units_sold: 5, volume_dollars: 25, case_cost: 10 })], {
          metadata: { opportunity: { id: "opp" } },
        }),
      ]
      expect(sku_plan_reference(roots, "sku:1")).toBeNull()
      expect(sku_plan_reference(roots, "sku:2")).toBeNull()
      expect(sku_plan_reference(roots, undefined)).toBeNull()
    })
  })
  describe("case_basis_hint", () => {
    it("spells out a tray in the plan's units, with the entry converted", () => {
      expect(
        case_basis_hint({ reference: { case_cost: 20.56, units_per_case: 4 }, cases_per_store: 3, price_per_case: 20.56, span: "week" }),
      ).toEqual({
        text: "In this plan 1 case of this SKU is 4 units, at $20.56 a case. 3 cases per store is 12 units per store per week.",
      })
    })

    it("says so when the plan counts a case as one unit", () => {
      // Sanzo before its multi-packs moved to the tray: a 6-pack was one unit and one case.
      expect(case_basis_hint({ reference: { case_cost: 5.14, units_per_case: 1 }, cases_per_store: 3, price_per_case: 5.14, span: "week" })).toEqual({
        text: "In this plan a case of this SKU is counted as 1 unit ($5.14), so cases and units are the same number here.",
      })
    })

    it("warns when the price typed is for a different pack than the plan's", () => {
      const hint = case_basis_hint({ reference: { case_cost: 5.14, units_per_case: 1 }, price_per_case: 20.56, span: "week" })
      expect(hint?.warning).toBe("The rest of this plan prices this SKU at $5.14 a case. $20.56 prices a different pack.")
      // A cent of rounding is not a different pack.
      expect(case_basis_hint({ reference: { case_cost: 20.56, units_per_case: 4 }, price_per_case: 20.6, span: "week" })?.warning).toBeUndefined()
    })

    it("falls back to the plan's price alone, then the catalog's pack, then nothing", () => {
      expect(case_basis_hint({ reference: { case_cost: 10, units_per_case: null }, span: "month" })).toEqual({
        text: "This plan prices a case of this SKU at $10.00.",
      })
      expect(case_basis_hint({ reference: null, catalog_units_per_case: 24, span: "month" })).toEqual({
        text: "The catalog's case for this SKU holds 24. Cases and price are both per that case.",
      })
      expect(case_basis_hint({ reference: null, span: "month" })).toBeNull()
    })
  })
  describe("implied_part_case", () => {
    it("reads the part-case off a zero-case row's dollars", () => {
      // One 6-pack of a 24-can tray: $5.14 of a $20.56 case.
      expect(implied_part_case(0, 5.14, 20.56)).toBe(0.25)
      expect(implied_part_case(0, 10.28, 20.56)).toBe(0.5)
    })

    it("is zero when the row has whole cases, or nothing implies a part-case", () => {
      expect(implied_part_case(279, 5736.24, 20.56)).toBe(0)
      expect(implied_part_case(0, 0, 20.56)).toBe(0)
      expect(implied_part_case(0, 514, 0)).toBe(0)
      expect(implied_part_case(undefined)).toBe(0)
      // Dollars worth 25 cases against a stored zero are not this row's cases × price.
      expect(implied_part_case(0, 514, 20.56)).toBe(0)
    })
  })

  describe("metrics_part_cases", () => {
    it("takes an aggregate's collected sum over anything its own numbers imply", () => {
      expect(metrics_part_cases({ cases_sold: 0, volume_dollars: 25.7, case_cost: 20.56, part_cases: 1.25 })).toBe(1.25)
      expect(metrics_part_cases({ cases_sold: 0, volume_dollars: 5.14, case_cost: 20.56 })).toBe(0.25)
      expect(metrics_part_cases(undefined)).toBe(0)
    })
  })

  describe("round_cases / format_cases", () => {
    it("keeps the fraction a per-store rate implies, to the hundredth", () => {
      expect(round_cases(7 * 1.2)).toBe(8.4)
      expect(round_cases(100 * 1.2 * 1.1 ** (11 / 12))).toBe(130.96)
      expect(round_cases(Number.NaN)).toBe(0)
      expect(format_cases(8.4)).toBe("8.4")
      expect(format_cases(1234)).toBe("1,234")
      expect(format_cases(0.333)).toBe("0.33")
    })
  })

  describe("cases_display", () => {
    it("shows the part-cases behind a zero to two decimals", () => {
      expect(cases_display(0, 0.25)).toBe("0.25")
      expect(cases_display(0, 0.5)).toBe("0.50")
      // Five SKUs of a quarter case each under one parent.
      expect(cases_display(0, 1.25)).toBe("1.25")
    })

    it("prints whole cases as they are, and a real zero as zero", () => {
      expect(cases_display(279, 0.25)).toBe("279")
      expect(cases_display(1116)).toBe((1116).toLocaleString())
      expect(cases_display(0, 0)).toBe("0")
      expect(cases_display(undefined)).toBe("0")
    })
  })
}
